import { Autocomplete, Button, Checkbox, FormControlLabel, IconButton, Stack, TextField, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import React from 'react';
import TableCell from '@mui/material/TableCell';
import TableRow from '../../components/table/TableRow';
import Device from '../../models/device';
import UserFetch from '../../userFetch';
import { User } from '../../models/user';
import { isValidMacAddress } from '../../utils';
import { permissionTableFields } from './permissionFieldsConfig';

function formatCell(value: string, field: keyof Device): React.ReactNode {
  // Convert MAC address to uppercase if it exists
  if (field === 'mac' && isValidMacAddress(value)) {
    value = value.toUpperCase();
  }

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (value) {
      navigator.clipboard.writeText(value);
    }
  };

  return (
    <TableCell
      align="left"
      onDoubleClick={handleDoubleClick}
      key={field}
    >
      {value}
    </TableCell>
  );
}

interface Permission {
  uuid: string;
  email: string;
  firstName: string;
  lastName: string;
  view: boolean;
  command: boolean;
  configuration: boolean;
}

interface PermissionRowProps {
  device: Device;
  selected: boolean;
  labelId: string;
  rowClicked?: (serial_number: string) => void;
  users: User[];
}

function PermissionRow(props: PermissionRowProps) {
  const [view, setView] = React.useState(false);
  const [command, setCommand] = React.useState(false);
  const [configuration, setConfiguration] = React.useState(false);
  const [userId, setUserId] = React.useState('');

  const [permissions, setPermissions] = React.useState([] as Permission[]);

  const [confirmDeleteId, setConfirmDeleteId] = React.useState<string | null>(null);

  const updatePermission = React.useCallback((opened = true) => {
    if (opened) {
      UserFetch(`/api/devices/${props.device.serial_number}/permissions`)
        .then((response) => response.json())
        .then((data) => {
          setPermissions(data);
        });
    }
  }, [props.device.serial_number]);

  const deletePermission = React.useCallback((userId: string) => {
    UserFetch(`/api/devices/${props.device.serial_number}/permissions/${userId}`, {
      method: 'DELETE'
    })
      .then((response) => {
        if (response.ok) {
          updatePermission();
        }
      });
  }, [props.device.serial_number, updatePermission]);

  const addPermission = React.useCallback((userId: string, view: boolean, command: boolean, configuration: boolean) => {
    UserFetch(`/api/devices/${props.device.serial_number}/permissions`, {
      method: 'POST',
      headers: {
        'Content-type': 'application/json; charset=UTF-8'
      },
      body: JSON.stringify({
        user_id: userId,
        view,
        command,
        configuration
      })
    })
      .then((response) => {
        if (response.ok) {
          updatePermission();
        }
      });
  }, [props.device.serial_number, userId, view, command, configuration, updatePermission]);

  return (
    <TableRow
      key={props.device.serial_number}
      data={props.device.serial_number}
      storagePrefix="perm_drawer"
      isSelected={props.selected}
      drawerColumnSpan={6}
      drawerOpened={updatePermission}
      drawerChildren={
        <Stack spacing={2} alignItems={'start'} justifyContent={'flex-start'} minWidth={800}>
          {permissions.map((permission) => {
            const isConfirming = confirmDeleteId === permission.uuid;
            return (
              <Stack
                key={permission.uuid}
                sx={{
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  padding: '8px'
                }}
                spacing={2}
                direction={'row'}
                alignItems={'center'}
                justifyContent={'space-between'}
                width={'100%'}>
                <Typography
                  sx={{ minWidth: 300 }}>
                  {permission.firstName} {permission.lastName} ({permission.email})
                </Typography>

                <FormControlLabel
                  control={<Checkbox readOnly checked={permission.view} disableRipple disableFocusRipple />}
                  label="View"
                />

                <FormControlLabel
                  control={<Checkbox readOnly={true} checked={permission.command} disableRipple disableFocusRipple />}
                  label="Command"
                />

                <FormControlLabel
                  control={<Checkbox readOnly checked={permission.configuration} disableRipple disableFocusRipple />}
                  label="Configuration"
                />

                {isConfirming ? (
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        deletePermission(permission.uuid);
                        setConfirmDeleteId(null);
                      }}
                    >
                      Confirm
                    </Button>
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDeleteId(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                ) : (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmDeleteId(permission.uuid);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Stack>);
          })}
          {<Stack
            component="form"
            sx={{
              padding: '8px'
            }}
            spacing={2}
            direction={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
            width={'100%'}
            onSubmit={(e: React.FormEvent) => {
              e.preventDefault();
              addPermission(userId, view, command, configuration);
            }}>
            <Autocomplete
              sx={{ minWidth: 300 }}
              getOptionLabel={option => option.email}
              options={props.users}
              renderInput={(params) => (
                <TextField {...params} label="Select Option" variant="outlined" />
              )}
              onChange={(_event, value) => {
                setUserId(value?.uuid || '');
              }}
              value={props.users.find(user => user.uuid === userId)}
            />
            <FormControlLabel
              control={<Checkbox onChange={(e) => { setView(e.target.checked); }} checked={view} />}
              label="View"
            />

            <FormControlLabel
              control={<Checkbox onChange={(e) => { setCommand(e.target.checked) }} checked={command} />}
              label="Command"
            />

            <FormControlLabel
              control={<Checkbox onChange={(e) => { setConfiguration(e.target.checked) }} checked={configuration} />}
              label="Configuration"
            />

            <IconButton
              size='small'
              type="submit">
              <AddIcon />
            </IconButton>
          </Stack>}
        </ Stack>
      }>
      {
        <TableCell padding="checkbox">
          <Checkbox
            checked={props.selected}
            onClick={(e) => {
              e.stopPropagation();
              if (props.rowClicked)
                props.rowClicked(props.device.serial_number);
            }}
          />
        </TableCell>}

      {
        /* Render cells from the configuration */
        permissionTableFields.filter(field => !!field).map((field) =>
          formatCell(String(props.device[field]), field)
        )
      }
    </ TableRow>
  );
}

export default PermissionRow;